/**
 * Terminal 82 - Notes HTML Renderers
 *
 * Pure string builders — no DOM manipulation.
 */

import type { NoteResponse } from '../../api/notes/lib/types';
import { escapeHtml } from '../utils/html';

/** Format relative time: "2h", "1d", "3d" */
function timeAgo(ms: number): string {
  const seconds = Math.floor((Date.now() - ms) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/** Truncate wallet for display: 0x1234...abcd */
function truncateWallet(wallet: string): string {
  return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
}

/** Render the full notes section HTML */
export function renderNotesSection(opts: {
  notes: NoteResponse[];
  total: number;
  hasMore: boolean;
  winnerId: string | null;
  userVotes: Set<string>;
  userNoteId: string | null;
  authenticated: boolean;
  date: string;
  isToday: boolean;
}): string {
  const { notes, total, hasMore, winnerId, userVotes, userNoteId, authenticated, date, isToday } = opts;
  const countLabel = total > 0 ? ` · ${total} note${total !== 1 ? 's' : ''}` : '';

  const subtitle = isToday
    ? `Top note gets minted into the NFT.`
    : `Community notes for ${escapeHtml(date)}${countLabel}`;

  let authSection = '';
  if (isToday) {
    authSection = authenticated ? renderForm(userNoteId) : renderLoginPrompt();
  }

  const lockedBanner = !isToday && total > 0
    ? `<div class="notes-locked">VOTING CLOSED${countLabel}</div>`
    : '';

  const emptyMsg = isToday
    ? `No notes yet. Be the first.`
    : `No notes were submitted for this date.`;

  return `
    <div class="notes-header">
      <h2 class="notes-title">DAILY NOTES</h2>
      <span class="notes-subtitle">${subtitle}</span>
    </div>
    ${authSection}
    ${lockedBanner}
    <div class="notes-list" id="notes-list">
      ${notes.length === 0
        ? `<div class="notes-empty">${emptyMsg}</div>`
        : renderNotesList(notes, winnerId, userVotes, userNoteId, authenticated, isToday)}
    </div>
    ${hasMore ? `
      <button class="notes-load-more" id="notes-load-more">
        LOAD MORE (${notes.length} of ${total})
      </button>
    ` : ''}
  `;
}

/** Render the note submission form (hidden if user already posted) */
function renderForm(userNoteId: string | null): string {
  if (userNoteId) return '';

  return `
    <form class="notes-form" id="notes-form">
      <textarea
        class="notes-textarea"
        id="note-text"
        placeholder="What happened today..."
        maxlength="500"
        rows="3"
      ></textarea>
      <div class="notes-form-row">
        <input
          type="text"
          class="notes-input"
          id="note-wallet"
          placeholder="0x... (required to receive NFT)"
          maxlength="42"
        />
        <input
          type="text"
          class="notes-input"
          id="note-twitter"
          placeholder="@handle (optional)"
          maxlength="16"
        />
        <button type="submit" class="notes-submit" id="note-submit">POST</button>
      </div>
    </form>
  `;
}

/** Render login prompt — user clicks Login in the nav bar */
function renderLoginPrompt(): string {
  return `
    <div class="notes-login-prompt">
      Sign in to write today's note
    </div>
  `;
}

/** Render the list of notes */
function renderNotesList(
  notes: NoteResponse[],
  winnerId: string | null,
  userVotes: Set<string>,
  userNoteId: string | null,
  authenticated: boolean,
  isToday: boolean,
): string {
  // Pin winner to top if present
  const winner = winnerId ? notes.find(n => n.id === winnerId) : null;
  const rest = winnerId ? notes.filter(n => n.id !== winnerId) : notes;
  const ordered = winner ? [winner, ...rest] : rest;

  return ordered.map(note => renderNoteCard(note, {
    isWinner: note.id === winnerId,
    isVoted: userVotes.has(note.id),
    isOwn: note.id === userNoteId,
    authenticated,
    isToday,
  })).join('');
}

/** Render a single note card */
function renderNoteCard(note: NoteResponse, opts: {
  isWinner: boolean;
  isVoted: boolean;
  isOwn: boolean;
  authenticated: boolean;
  isToday: boolean;
}): string {
  const { isWinner, isVoted, isOwn, authenticated, isToday } = opts;
  const votedClass = isVoted ? 'voted' : '';
  const winnerBadge = isWinner ? '<span class="note-winner-badge">SELECTED</span>' : '';

  const meta: string[] = [];
  
  // Display agent name or twitter handle with SVG icons
  if (note.type === 'agent' && note.agentName) {
    const robotIcon = `<svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor"><rect x="3" y="3" width="10" height="8" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="5" y="5" width="2" height="2"/><rect x="9" y="5" width="2" height="2"/><rect x="6" y="8" width="4" height="1"/><rect x="7" y="1" width="2" height="2"/></svg>`;
    meta.push(`${robotIcon} ${escapeHtml(note.agentName)}`);
  } else if (note.twitter) {
    const userIcon = `<svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor"><circle cx="8" cy="6" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M2 14s1-3 6-3 6 3 6 3" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>`;
    meta.push(`${userIcon} @${escapeHtml(note.twitter)}`);
  }
  
  if (note.wallet) meta.push(truncateWallet(escapeHtml(note.wallet)));
  meta.push(timeAgo(note.createdAt));

  const voteDisabled = !isToday || !authenticated || isOwn;

  const isAgent = note.type === 'agent';
  const agentBadge = isAgent ? '<span class="agent-badge">AI</span>' : '';

  return `
    <div class="note-card ${isWinner ? 'winner' : ''} ${isOwn ? 'own' : ''} ${isAgent ? 'agent' : 'human'}" data-note-id="${escapeHtml(note.id)}">
      <div class="note-body">
        ${winnerBadge}
        ${agentBadge}
        <p class="note-text">${escapeHtml(note.text)}</p>
        <span class="note-meta">${meta.join(' · ')}</span>
      </div>
      <div class="note-actions">
        <button
          class="note-vote-btn ${votedClass}"
          data-note-id="${escapeHtml(note.id)}"
          ${voteDisabled ? 'disabled' : ''}
          title="${!isToday ? 'Voting closed' : (isOwn ? 'Cannot vote on own note' : (isVoted ? 'Remove vote' : 'Vote'))}"
        >
          <span class="vote-arrow">${isVoted 
            ? `<svg viewBox="0 0 16 16" width="18" height="18" fill="currentColor"><path d="M8 2l6 6H10v6H6V8H2z"/></svg>`
            : `<svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 2l6 6H10v6H6V8H2z"/></svg>`
          }</span>
          <span class="vote-count" data-note-id="${escapeHtml(note.id)}">${note.voteCount}</span>
        </button>
        ${isOwn && isToday ? `<button class="note-delete-btn" data-note-id="${escapeHtml(note.id)}" title="Delete note"><svg viewBox="0 0 16 16" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M4 4l8 8M12 4l-8 8"/></svg></button>` : ''}
      </div>
    </div>
  `;
}
